import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import {
  LibraryItem,
  LibraryResponse,
  LibraryListParams,
} from "../models/api-types";
import useAuthStore from "@/store/use-auth-store";

// Regular query hook (for backward compatibility)
export const useLibrary = (params: LibraryListParams = {}) => {
  const { isAuthenticated } = useAuthStore();

  return useQuery<LibraryResponse>({
    queryKey: ["library", params],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.getLibrary, {
        params,
      });

      const data = (response as any).data;

      if (data?.data) {
        return data.data as LibraryResponse;
      }

      if (data?.result) {
        return data as LibraryResponse;
      }

      throw new Error("Invalid API response structure");
    },
    enabled: isAuthenticated(), // Sadece auth olan kullanıcılar için
    staleTime: 5 * 60 * 1000, // 5 dakika cache
    gcTime: 10 * 60 * 1000, // 10 dakika garbage collection
    retry: 2, // 2 kez tekrar dene
  });
};

// Tek bir library öğesi (detay ekranı direkt linkle açıldığında)
export const useLibraryItem = (libraryItemId: string | null) => {
  const { isAuthenticated } = useAuthStore();

  return useQuery<LibraryItem>({
    queryKey: ["library", "item", libraryItemId],
    queryFn: async () => {
      const response = await apiClient.get<{ data: LibraryItem }>(
        API_ENDPOINTS.libraryItem(libraryItemId as string)
      );
      return response.data;
    },
    enabled: isAuthenticated() && !!libraryItemId,
    staleTime: 5 * 60 * 1000,
  });
};

// Infinite query hook for library with pagination
export const useLibraryInfinite = (
  params: Omit<LibraryListParams, "page"> = {}
) => {
  const { isAuthenticated } = useAuthStore();

  return useInfiniteQuery<LibraryResponse>({
    queryKey: ["library", "infinite", params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.get(API_ENDPOINTS.getLibrary, {
        params: {
          ...params,
          page: pageParam,
          limit: params.limit || 12,
        },
      });

      const data = (response as any).data;

      if (data?.data) {
        return data.data as LibraryResponse;
      }

      if (data?.result) {
        return data as LibraryResponse;
      }

      throw new Error("Invalid API response structure");
    },
    getNextPageParam: (lastPage) => {
      const { page, totalPage } = lastPage.meta;
      return page < totalPage ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated(), // Sadece auth olan kullanıcılar için
    staleTime: 5 * 60 * 1000, // 5 dakika cache
    gcTime: 10 * 60 * 1000, // 10 dakika garbage collection
    retry: 2, // 2 kez tekrar dene
  });
};
